import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { fetchInstitutions } from "../lib/api";
import { ArrowUpRight, ShieldCheck, AlertCircle } from "lucide-react";
import { slugify } from "./Seo";

const Card = ({ it }) => {
  const typeSlug = it.type === "Bank" ? "bank" : "nbfc";
  return (
    <Link
      to={`/${typeSlug}/${it.slug}`}
      data-testid={`related-${it.slug}`}
      className="group block border border-[#E5E7EB] hover:border-[#0A0A0A] bg-white p-4 transition-colors"
    >
      <div className="flex items-start justify-between gap-2 mb-2">
        <span className="font-mono text-[9px] uppercase tracking-[0.2em] text-[#4B5563]">
          {it.category}
        </span>
        {it.has_detailed_contacts ? (
          <ShieldCheck className="w-3.5 h-3.5 text-emerald-600 flex-shrink-0" />
        ) : (
          <AlertCircle className="w-3.5 h-3.5 text-amber-500 flex-shrink-0" />
        )}
      </div>
      <div className="font-display font-bold text-sm leading-tight group-hover:text-[#002FA7] transition-colors">
        {it.name}
      </div>
      <div className="mt-1 font-mono text-[10px] text-[#4B5563]">
        {it.hq_city}, {it.hq_state}
      </div>
      <div className="mt-3 flex items-center gap-1 font-mono text-[10px] uppercase tracking-[0.15em] text-[#0A0A0A] group-hover:text-[#002FA7]">
        View contacts <ArrowUpRight className="w-3 h-3" />
      </div>
    </Link>
  );
};

const Section = ({ heading, subheading, items, ctaText, ctaPath }) => {
  if (!items || items.length === 0) return null;
  return (
    <div>
      <div className="flex items-end justify-between mb-4 border-b border-[#0A0A0A] pb-2">
        <div>
          <h2 className="font-display font-bold text-xl md:text-2xl tracking-tight">
            {heading}
          </h2>
          {subheading && (
            <p className="text-sm text-[#4B5563] mt-1">{subheading}</p>
          )}
        </div>
        {ctaPath && (
          <Link
            to={ctaPath}
            className="hidden sm:inline-flex items-center gap-1 font-mono text-[11px] uppercase tracking-[0.15em] text-[#0A0A0A] hover:text-[#002FA7]"
          >
            {ctaText} <ArrowUpRight className="w-3 h-3" />
          </Link>
        )}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
        {items.map((it) => <Card key={it.id} it={it} />)}
      </div>
    </div>
  );
};

export const RelatedInstitutions = ({ category, state, type, excludeSlug }) => {
  const [sameCategory, setSameCategory] = useState([]);
  const [sameState, setSameState] = useState([]);
  const [sameType, setSameType] = useState([]);

  useEffect(() => {
    if (!category && !state && !type) return;
    const exclude = (list) =>
      (list || []).filter((x) => x.slug && x.slug !== excludeSlug).slice(0, 8);

    const tasks = [];
    if (category) {
      tasks.push(
        fetchInstitutions({ category, page_size: 12 })
          .then((r) => setSameCategory(exclude(r.results)))
          .catch(() => {})
      );
    }
    if (state) {
      tasks.push(
        fetchInstitutions({ state, page_size: 12 })
          .then((r) => setSameState(exclude(r.results)))
          .catch(() => {})
      );
    }
    if (type) {
      tasks.push(
        fetchInstitutions({ type, detailed: "verified", page_size: 12 })
          .then((r) => setSameType(exclude(r.results)))
          .catch(() => {})
      );
    }
    Promise.all(tasks);
  }, [category, state, type, excludeSlug]);

  const hasContent = sameCategory.length + sameState.length + sameType.length > 0;
  if (!hasContent) return null;

  return (
    <section
      data-testid="related-institutions"
      className="mt-16 border-t-2 border-[#0A0A0A] pt-12 space-y-12"
      aria-labelledby="related-heading"
    >
      <div>
        <div className="flex items-center gap-3 mb-2">
          <span className="w-8 h-px bg-[#0A0A0A]" />
          <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#4B5563]">
            Related institutions
          </span>
        </div>
        <h2 id="related-heading" className="sr-only">Related Institutions</h2>
      </div>

      <Section
        heading={`Other ${category || "institutions"}`}
        subheading={`More ${category || "entities"} on BankDesks.com — all with RBI-linked escalation contacts.`}
        items={sameCategory}
        ctaText="See all"
        ctaPath={`/category/${slugify(category || "")}`}
      />

      <Section
        heading={`Banks & NBFCs in ${state || "the same state"}`}
        subheading={`Other regulated institutions headquartered in ${state}.`}
        items={sameState}
        ctaText={`All in ${state}`}
        ctaPath={`/state/${slugify(state || "")}`}
      />

      <Section
        heading={`Popular verified ${type === "Bank" ? "banks" : "NBFCs"}`}
        subheading="Frequently searched entries with fully curated Recovery & Legal contacts."
        items={sameType}
      />
    </section>
  );
};
